import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api.tsx";
import Product from "../Entities/Product.tsx";
import Campaign from "../Entities/Campaign.tsx";
import { getDiscountedPrice } from "../utils/price.tsx";
import { useCart } from "../contexts/CartContext.tsx";
import { useError } from "../contexts/ErrorContext.tsx";
import { useSuccess } from "../contexts/SuccessContext.tsx";

const ProductDetailPage = () => {
    const { id } = useParams();
    const [product, setProduct] = useState<Product | null>(null);
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const { addToCart } = useCart();
    const { setError } = useError();
    const { addSuccess } = useSuccess();
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState<"features" | "description" | "shipping" | "payment">("features");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [productRes, campaignRes] = await Promise.all([
                    api.get(`/api/product/${id}`),
                    api.get(`/api/campaign`)
                ]);
                setProduct(productRes.data);
                const activeCampaign = campaignRes.data.find((c: Campaign) =>
                    c.productIds.includes(productRes.data.id) &&
                    new Date(c.startDate) <= new Date() &&
                    new Date(c.endDate) >= new Date()
                );
                setCampaign(activeCampaign || null);
            } catch (error) {
                setError(error.response?.data?.Message || "Ürün bilgisi alınamadı.");
            }
        };
        fetchData();
    }, [id, setError]);

    if (!product) return <div className="p-8">Ürün yükleniyor...</div>;

    const discountedPrice = campaign
        ? getDiscountedPrice(product.price, campaign.discountRate)
        : product.price;

    // Eksik alanlar için örnek dummy veriler
    const productCode = product.code || "GRM12"; // Ürün kodu
    const shortDescription = product.description || "Kırılmaz Melamin Çorba Kasesi 12cm";
    const technicalDetails = product.technicalDetails || [
        { label: "Çap", value: "12 cm" },
        { label: "Malzeme", value: "Melamin" },
        { label: "Renk", value: "Beyaz" },
        { label: "Kırılmaz", value: "Evet" }
    ];
    const shippingInfo = "Tahmini teslimat: 1-3 iş günü içinde kargoda";
    const isInStock = product.stock > 0;
    const technicalDetailsRaw = product.technicalDetails || "";
    const technicalDetailsLines = technicalDetailsRaw.split("\n").filter(Boolean);

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Ürün görseli ve galeri */}
                <div className="flex flex-col items-center">
                    <div className="border rounded-lg shadow h-80 w-full bg-gray-50 flex items-center justify-center mb-4">
                        <img
                            src={product.imageUrl || "/logo192.png"}
                            alt={product.name}
                            className="max-h-full max-w-full object-contain"
                        />
                    </div>
                    {/* Galeri için küçük görseller (dummy) */}
                    <div className="flex gap-2 mt-2">
                        {[product.imageUrl, product.imageUrl, product.imageUrl].map((img, i) => (
                            <img
                                key={i}
                                src={img || "/logo192.png"}
                                alt={`Galeri ${i + 1}`}
                                className="w-16 h-16 object-cover rounded border cursor-pointer"
                            />
                        ))}
                    </div>
                    {/* Favori ve paylaşım */}
                    <div className="flex gap-4 mt-4">
                        <button className="flex items-center gap-1 text-red-500 hover:text-red-700">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M12 21l-1.45-1.32C5.4 15.36 2 12.28 2 8.5A5.5 5.5 0 017.5 3c1.74 0 3.41 0.81 4.5 2.09A6.5 6.5 0 0116.5 3 5.5 5.5 0 0122 8.5c0 3.78-3.4 6.86-8.55 11.18L12 21z" /></svg>
                            Favorilere Ekle
                        </button>
                        <button className="flex items-center gap-1 text-blue-600 hover:text-blue-800">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.36 6.64a9 9 0 11-12.73 0" /></svg>
                            Paylaş
                        </button>
                    </div>
                </div>

                {/* Ürün bilgileri */}
                <div>
                    <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
                    <div className="flex items-center gap-4 mb-2">
                        <span className="text-gray-500 text-sm">Ürün Kodu: <span className="font-semibold">{productCode}</span></span>
                        <span className={`text-xs px-2 py-1 rounded ${isInStock ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {isInStock ? "Stokta var" : "Stok yok"}
                        </span>
                    </div>
                    <p className="text-gray-700 mb-4">{shortDescription}</p>
                    {campaign && (
                        <div className="mb-4 bg-yellow-100 p-3 rounded">
                            <h2 className="font-semibold text-lg mb-2">
                                %{campaign.discountRate} İndirimli Kampanya!
                            </h2>
                            <p className="text-sm mb-1">
                                Kampanya bitiş tarihi: {new Date(campaign.endDate).toLocaleDateString("tr-TR")}
                            </p>
                            <p className="text-sm">
                                Katılım: {campaign.participantCount || 0} / {campaign.maxUserCount}
                            </p>
                            <p className="text-sm">
                                Minimum sipariş adedi: {campaign.minTotalOrderQuantity} • Maksimum: {campaign.maxTotalOrderQuantity}
                            </p>
                            <p className="text-sm">
                                Ön ödeme oranı: %{(campaign.depositRate).toFixed(0)}
                            </p>
                        </div>
                    )}

                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-2xl font-semibold text-green-700">{discountedPrice} TL</span>
                        {campaign && (
                            <span className="line-through text-gray-500 text-lg ml-2">{product.price} TL</span>
                        )}
                    </div>

                    {/* Adet seçici ve sepete ekle */}
                    <div className="flex items-center gap-2 mb-4">
                        <button
                            type="button"
                            className="px-3 py-1 bg-gray-200 rounded text-lg"
                            onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        >-</button>
                        <input
                            type="number"
                            min={1}
                            max={product.stock}
                            value={quantity}
                            onChange={e => setQuantity(Math.max(1, Math.min(product.stock, Number(e.target.value))))}
                            className="w-16 text-center border rounded"
                        />
                        <button
                            type="button"
                            className="px-3 py-1 bg-gray-200 rounded text-lg"
                            onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                        >+</button>
                        <span className="text-xs text-gray-500 ml-2">Stok: {product.stock}</span>
                    </div>

                    <button
                        onClick={async () => {
                            const response = await addToCart({
                                id: product.id,
                                productId: product.id,
                                name: product.name,
                                price: product.price,
                                finalPrice: discountedPrice,
                                stock: product.stock,
                                quantity,
                                ...(campaign && { campaignId: campaign.id }),
                                depositRate: campaign?.depositRate || 0
                            });
                            if (response?.success || response?.status === 200) {
                                addSuccess("Ürün sepete eklendi!");
                            }
                        }}
                        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 font-semibold text-lg"
                        disabled={!isInStock}
                    >
                        Sepete Ekle
                    </button>

                    {/* Kargo bilgisi */}
                    <div className="mt-4 bg-gray-100 rounded p-3 text-sm text-gray-700 flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>
                        {shippingInfo}
                    </div>
                </div>
            </div>

            {/* Teknik özellikler kutusu */}
            <div className="max-w-3xl mx-auto mt-8 bg-white rounded shadow p-6">
                <h3 className="font-bold text-lg mb-3">Özellikler</h3>
                <ul className="list-disc list-inside space-y-2">
                    {technicalDetailsLines.map((line, i) => (
                        <li key={i} className="whitespace-pre-line text-gray-700">{line}</li>
                    ))}
                </ul>
            </div>

            {/* Alt kısım: Tablar ve ödeme seçenekleri yan yana */}
            <div className="flex flex-col items-center justify-center mt-10">
                <div className="w-full max-w-4xl bg-white rounded shadow p-4">
                    <div className="flex gap-2 mb-6 border-b justify-center overflow-x-auto whitespace-nowrap scrollbar-hide">
                        <button
                            className={`py-2 px-3 text-sm font-semibold border-b-2 transition ${activeTab === "features"
                                ? "border-blue-600 text-blue-700"
                                : "border-transparent text-gray-600"
                                }`}
                            onClick={() => setActiveTab("features")}
                        >
                            Özellikler
                        </button>
                        <button
                            className={`py-2 px-3 text-sm font-semibold border-b-2 transition ${activeTab === "payment"
                                ? "border-blue-600 text-blue-700"
                                : "border-transparent text-gray-600"
                                }`}
                            onClick={() => setActiveTab("payment")}
                        >
                            Ödeme Seçenekleri
                        </button>
                        <button
                            className={`py-2 px-3 text-sm font-semibold border-b-2 transition ${activeTab === "description"
                                ? "border-blue-600 text-blue-700"
                                : "border-transparent text-gray-600"
                                }`}
                            onClick={() => setActiveTab("description")}
                        >
                            Açıklama
                        </button>
                        <button
                            className={`py-2 px-3 text-sm font-semibold border-b-2 transition ${activeTab === "shipping"
                                ? "border-blue-600 text-blue-700"
                                : "border-transparent text-gray-600"
                                }`}
                            onClick={() => setActiveTab("shipping")}
                        >
                            Kargo
                        </button>
                    </div>
                    <div>
                        {activeTab === "features" && (
                            <ul className="list-disc list-inside space-y-2">
                                {technicalDetailsLines.map((detail, i) => (
                                    <li key={i}>
                                        <span className="font-semibold">{detail}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {activeTab === "description" && (
                            <div className="text-gray-700 text-sm">{shortDescription}</div>
                        )}
                        {activeTab === "shipping" && (
                            <div className="text-gray-700 text-sm">{shippingInfo}</div>
                        )}
                        {activeTab === "payment" && (
                            <>
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                    <span>Kredi Kartı ile Peşin</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                    <span>Havale/EFT</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                    <span>Kapıda Ödeme</span>
                                </div>
                                {campaign && (
                                    <div className="flex items-center gap-2 mt-2">
                                        <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2" /></svg>
                                        <span>
                                            Kampanya ön ödeme oranı: <span className="font-bold text-yellow-700">%{campaign.depositRate}</span>
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailPage;